import NiceModal from '@ebay/nice-modal-react';
import { Document, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import { Buffer } from 'buffer';

import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useMediaQuery from 'hooks/useMediaQuery';

import { setPrintLocale } from 'pages/Settings/Print/PrintSettingsSlice';
import usePrintUtils from 'pages/Settings/Print/hooks/usePrintUtils';

import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';

import printStyles from './defaultStyles';
import usePrintLocale from './usePrintLocale';

/**
 *
 * @author  @erendeveci
 */

const ReturnShipmentDetailsTemplate = ({ orderDetails }) => {
	const { printLocale } = usePrintLocale();
	const { t } = useTranslation('translation', {
		lng: printLocale
	});

	const contentColumnsStyles = useMemo(() => ['20%', '30%', '50%', '12%', '14%', '100%'], []);
	const pageSize = 'A4';
	const pageOrientation = 'portrait';
	const styles = StyleSheet.create({
		section: {
			padding: 2,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center'
		},
		table: {
			display: 'table',
			width: 'auto',
			borderStyle: 'solid',
			borderWidth: 1,
			borderRightWidth: 1,
			borderLeftWidth: 0,
			borderBottomWidth: 0,
			borderTopWidth: 1,
			borderColor: '#ccc',
			marginBottom: 10
		},
		tableRow: {
			flexDirection: printLocale === 'arabicValue' ? 'row-reverse' : 'row',
			height: 'auto'
		},
		tableColHeader: {
			width: '100%',
			borderStyle: 'solid',
			borderWidth: 1,
			borderLeftWidth: 1,
			borderRightWidth: 0,
			borderTopWidth: 0,
			borderColor: '#ccc',
			textAlign: 'center',
			alignItems: 'center',
			padding: 2,
			backgroundColor: '#D9D9D9'
		},
		tableCol: {
			borderStyle: 'solid',
			borderWidth: 1,
			borderLeftWidth: 1,
			borderRightWidth: 0,
			borderTopWidth: 0,
			borderColor: '#ccc'
		},
		tableColCenter: {
			borderStyle: 'solid',
			borderWidth: 1,
			borderLeftWidth: 1,
			borderRightWidth: 0,
			borderTopWidth: 0,
			borderColor: '#ccc',
			textAlign: 'center',
			alignItems: 'center',
			padding: 2,
			backgroundColor: '#D9D9D9'
		},
		tableCell: {
			margin: 'auto 10',
			marginTop: 0,
			textAlign: printLocale === 'arabicValue' ? 'right' : 'left'
		},
		tableCellFull: {
			width: '100%',
			margin: 'auto 10',
			marginTop: 0,
			textAlign: printLocale === 'arabicValue' ? 'right' : 'left'
		},
		logo: {
			width: 19,
			height: 19
		},

		header: { fontSize: 14 },

		page: {
			paddingTop: 15,
			paddingHorizontal: 20,
			paddingBottom: 125,
			fontSize: 10,
			fontFamily: printStyles.text.fontFamily,
			color: '#000',
			fontWeight: 600
		},
		footer: {
			position: 'absolute',
			bottom: 10,
			left: '45%'
		},
		signature: {
			marginTop: 10,
			marginBottom: 30,
			position: 'absolute',
			bottom: 20,
			left: '3.5%'
		},
		pageNumber: {
			position: 'absolute',
			right: 10,
			bottom: 10,
			display: 'flex',
			flexDirection: 'row'
		}
	});

	return (
		<Document>
			<Page size={pageSize} orientation={pageOrientation} style={styles.page} wrap>
				{/** header */}
				<View>
					<View style={[styles.section, styles.header]}>
						<Text style={printStyles.textArabic}>{t('expressReturnShipmentDetails')}</Text>
					</View>
					{/** first section */}
					<View style={[styles.table]}>
						{/** table header */}
						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}></View>
							<View
								style={[
									styles.tableColCenter,
									{ width: contentColumnsStyles[2], textAlign: 'center' }
								]}>
								<Text style={[styles.tableCell]}>{t('shipperInformation')}</Text>
							</View>
							<View style={[styles.tableColCenter, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{t('consigneeInformation')}</Text>
							</View>
						</View>

						{/** information*/}

						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('name')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.clientName}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.receiverName}</Text>
							</View>
						</View>
						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('address')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.senderAddress}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.receiverAddress}</Text>
							</View>
						</View>
						<View style={[styles.tableRow, { borderTop: 0 }]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('telephone')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.senderTelephone}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.receiverTelephone}</Text>
							</View>
						</View>
					</View>

					{/** second section */}
					<View style={[styles.table]}>
						{/** table header */}
						<View style={[styles.tableRow]}>
							<View style={[styles.tableColHeader]}>
								<Text style={[styles.tableCell]}>{t('shipmentDetails')}</Text>
							</View>
						</View>

						{/** information*/}

						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('orderID')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[5] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.orderID}</Text>
							</View>
						</View>
						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('orderCreationDate')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[5] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.shipmentCreateDate}</Text>
							</View>
						</View>
						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('grandTotal')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[5] }]}>
								<Text style={[styles.tableCell]}>
									{orderDetails?.grandTotal} &nbsp; ({orderDetails?.currency})
								</Text>
							</View>
						</View>
						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('numberOfItems')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[5] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.items?.length}</Text>
							</View>
						</View>
						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('shipmentNumberAwb')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[5] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.shipmentNumber}</Text>
							</View>
						</View>
						<View style={[styles.tableRow]}>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('deliveryName')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[5] }]}>
								<Text style={[styles.tableCell]}>{orderDetails?.shippingCompanyName}</Text>
							</View>
						</View>
					</View>

					{/** third section */}
					<View style={[styles.table]}>
						{/** table header */}
						<View style={[styles.tableRow]} fixed>
							<View style={[styles.tableColHeader]}>
								<Text style={[styles.tableCell]}>{t('productDetails')}</Text>
							</View>
						</View>

						{/** information*/}

						<View style={[styles.tableRow]} fixed>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('sku')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('productName')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
								<Text style={[styles.tableCell]}>{t('quantity')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
								<Text style={[styles.tableCell]}>{t('unitPrice')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
								<Text style={[styles.tableCell]}>{t('total')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('productStatus')}</Text>
							</View>
						</View>
						{!orderDetails?.items && (
							<View style={[styles.tableRow, { height: 25 }]}>
								<View style={[styles.tableCol, { width: '100%' }]}>
									<Text style={[styles.tableCell]}></Text>
								</View>
							</View>
						)}
						{orderDetails?.items?.map((item, index) => (
							<View key={index} style={[styles.tableRow]}>
								<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
									<Text style={[styles.tableCell]}>{item.sku}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
									<Text style={[styles.tableCell]}>{item.name}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
									<Text style={[styles.tableCell]}>{item.quantity}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
									<Text style={[styles.tableCell]}>{item.price}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
									<Text style={[styles.tableCell]}>{item.total}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
									<Text style={[styles.tableCell]}>{item.status}</Text>
								</View>
							</View>
						))}
					</View>

					{/** fourth section */}
					<View style={[styles.table]}>
						{/** table header */}
						<View style={[styles.tableRow]} fixed>
							<View style={[styles.tableColHeader]}>
								<Text style={[styles.tableCell]}>{t('damagedItems')}</Text>
							</View>
						</View>

						{/** information*/}

						<View style={[styles.tableRow]} fixed>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('productName')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('quantity')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('unitPrice')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('total')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('damageDescription')}</Text>
							</View>
						</View>
						{!orderDetails?.items && (
							<View style={[styles.tableRow, { height: 25 }]}>
								<View style={[styles.tableCol, { width: '100%' }]}>
									<Text style={[styles.tableCell]}></Text>
								</View>
							</View>
						)}
						{orderDetails?.items?.map((item, index) => (
							<View key={index} style={[styles.tableRow]}>
								<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
									<Text style={[styles.tableCell]}>&nbsp;</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
									<Text style={[styles.tableCell]}>&nbsp;</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
									<Text style={[styles.tableCell]}>&nbsp;</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
									<Text style={[styles.tableCell]}>&nbsp;</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
									<Text style={[styles.tableCell]}>&nbsp;</Text>
								</View>
							</View>
						))}
						<View style={[styles.tableRow]} fixed>
							<View style={[styles.tableCol, { width: '60%', alignItems: 'center' }]}>
								<Text style={[styles.tableCell]}>
									{t('totalDamage')} &nbsp; ({orderDetails?.currency})
								</Text>
							</View>
							<View style={[styles.tableCol, { width: '20%' }]}>
								<Text style={[styles.tableCell]}>&nbsp;</Text>
							</View>
							<View style={[styles.tableCol, { width: '20%', backgroundColor: '#D9D9D9' }]}>
								<Text style={[styles.tableCell]}>&nbsp;</Text>
							</View>
						</View>
					</View>
				</View>
				{/** sixth section, signature */}
				<View style={[styles.table, styles.signature]} wrap={false} fixed>
					<View style={[styles.tableRow, { height: 50 }]}>
						<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
							<Text style={[styles.tableCell]}>{t('courierNameSignatureDescription')}</Text>
						</View>

						<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
							<Text style={[styles.tableCell]}>{t('courierNameSignature')}</Text>
						</View>
					</View>
				</View>
				<View style={[styles.footer]} fixed>
					<View style={[styles.tableRow]}>
						<Text> {t('poweredBy')} </Text>
						<Image
							src="https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png"
							style={styles.logo}
						/>
					</View>
				</View>
				<Text
					style={styles.pageNumber}
					render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
					fixed
				/>
			</Page>
		</Document>
	);
};

const ReturnShipmentDetails = NiceModal.create(({ ids }) => {
	// States
	const [orderData, setOrderData] = useState(null);

	// Print utils
	const { handlePDFDownload, isLoading, pdfFile, mergePDF, fetchOrderPrintData, renderPDFChunks } = usePrintUtils();

	// Hooks initialize
	const { mobile = false } = useSelector((state) => state?.layout);
	const isMobileDevice = useMediaQuery(Breakpoints.mobile);

	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { fetchLanguage } = usePrintLocale();
	const { printLocale } = useSelector((state) => state?.printSettings);
	const langs = useMemo(() => languages.map((language) => language.code), []);

	// Fetch the print data of the order
	const getPrintData = useCallback(
		async (ids) => {
			if (!ids) return;

			await fetchOrderPrintData(ids, false, null, true).then((data) => {
				if (data) {
					setOrderData(data);
				}
			});
		},
		[ids]
	);

	// Effects
	useEffect(() => {
		getPrintData(ids);
	}, [ids]);

	useEffect(() => {
		if (!printLocale || !orderData) return;

		fetchLanguage(printLocale).then(() => createPDF(orderData));
	}, [printLocale, orderData]);

	const renderPDF = useCallback(({ key, ...props }) => {
		return pdf(createElement(ReturnShipmentDetailsTemplate, props, key)).toBlob();
	}, []);

	// Create pdf
	const createPDF = async (orderData) => {
		if (!orderData) return;

		await mergePDF(async (merger) => {
			const blobData = [];
			for (const order of orderData) {
				blobData.push({
					orderDetails: order,
					key: order?.orderID
				});
			}

			const pdfBlobs = await renderPDFChunks(blobData, 25, renderPDF);
			for (const pdfBlob of pdfBlobs) {
				await merger.add(pdfBlob);
			}

			if (window.ReactNativeWebView) {
				const buffer = await merger.saveAsBuffer();
				const u8 = new Uint8Array(buffer);
				const b64 = Buffer.from(u8).toString('base64');
				window.ReactNativeWebView.postMessage(b64);
			} else {
				const mergedPdf = await merger.saveAsBlob();
				return mergedPdf;
			}
		});
	};

	// Follows language change
	const handleLanguageChange = useCallback(
		async (value) => {
			dispatch(setPrintLocale(value));
		},
		[printLocale]
	);

	const defaultColSpan = {
		md: 2
	};

	return !isLoading ? (
		<Modal closeButton maxWidth="x-large" fullwidth className="modal-ReturnShipmentDetails">
			<ModalTitle>
				<Typography variant="p1">print</Typography>
				<FormItem colSpan={defaultColSpan}>
					<EnumSelect
						valueRemovable={false}
						data={langs}
						value={printLocale}
						setValue={(value) => {
							handleLanguageChange(value);
						}}
						searchable={false}
					/>
				</FormItem>
				{mobile && pdfFile && (
					<FormItem
						colSpan={{
							md: 2
						}}>
						<Button
							fullWidth={true}
							onClick={() => handlePDFDownload(pdfFile, `ReturnShipmentDetails-${ids.join('-')}`)}>
							{t('download')}
						</Button>
					</FormItem>
				)}
			</ModalTitle>
			{!isMobileDevice && (
				<ModalContent
					style={{
						height: '80dvh'
					}}>
					{pdfFile ? (
						<div
							style={{
								height: '100%'
							}}
							dangerouslySetInnerHTML={{
								__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
							}}
						/>
					) : (
						<Loading />
					)}
				</ModalContent>
			)}
		</Modal>
	) : (
		<Loading />
	);
});

export default ReturnShipmentDetails;
